import React, { useEffect, useRef, useState } from 'react';

import Image from 'next/image';

import arrowIcon from '@/assets/icons/dashboard-slide-arrow-icon.svg';
import cn from '@/helpers/utils/classNames';

interface TabHeaderProps {
  list: any[];
  activeTab: string;
  setActiveTab: (tabName: string) => void;
  stretchTabs?: boolean;
  leaderboard?: boolean;
}

const TabHeader = ({ list, activeTab, setActiveTab, stretchTabs, leaderboard }: TabHeaderProps) => {
  const containerRef = useRef<HTMLUListElement>(null);
  const tabRefs = useRef<{ [key: string]: HTMLLIElement | null }>({});
  const [isManualTabChange, setIsManualTabChange] = useState(false);
  const initialized = useRef(false);

  // Scroll handler for arrows
  const scrollByAmount = (amount: number) => {
    containerRef.current?.scrollBy({ left: amount, behavior: 'smooth' });
  };

  // Tab click handler
  const handleTabClick = (tabKey: string) => {
    setIsManualTabChange(true);
    setActiveTab(tabKey);
  };

  // Scroll to active tab only after manual interaction
  useEffect(() => {
    if (!initialized.current) {
      // First render: Center the active tab without animation
      const selectedTabEl = tabRefs.current[activeTab];
      if (selectedTabEl && containerRef.current) {
        const container = containerRef.current;
        const tabLeft = selectedTabEl.offsetLeft;
        const tabWidth = selectedTabEl.offsetWidth;
        const containerWidth = container.offsetWidth;
        container.scrollLeft = tabLeft + tabWidth / 2 - containerWidth / 2;
      }
      initialized.current = true;
      return;
    }

    if (isManualTabChange) {
      // Subsequent changes: Smooth scroll
      const selectedTabEl = tabRefs.current[activeTab];
      selectedTabEl?.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest',
      });
      setIsManualTabChange(false);
    }
  }, [activeTab, isManualTabChange]);

  return (
    <div className="v2-flex v2-items-center v2-gap-1 v2-w-full v2-relative">
      {/* Left arrow */}
      <div className="v2-flex-shrink-0">
        <Image
          src={arrowIcon}
          alt="Scroll Left"
          role="button"
          onClick={() => {
            const i = list.findIndex(item => item.key === activeTab);
            if (i > 0) handleTabClick(list[i - 1].key);
            scrollByAmount(-150);
          }}
        />
      </div>

      {/* Tabs container */}
      <ul
        ref={containerRef}
        className="v2-flex v2-overflow-x-auto v2-w-full no-scrollbar v2-snap-x v2-snap-mandatory v2-relative v2-border-b v2-border-[#E5E5E5] v2-min-w-0"
      >
        {list.map(item => {
          if (leaderboard) {
            return (
              <li
                key={item.key}
                ref={el => (tabRefs.current[item.key] = el)}
                className={cn(
                  'v2-relative v2-h-[51px] v2-flex v2-items-center v2-justify-center v2-py-3 v2-text-center v2-font-sweet-sans-pro-medium v2-font-medium v2-text-sm v2-text-[#475467] v2-cursor-pointer',
                  stretchTabs ? 'v2-flex-1' : 'v2-w-max',
                  {
                    '!v2-text-[#154734] !v2-border-b-[2px] !v2-border-[#154734] v2-bg-[#E2E8E6]':
                      activeTab === item.key,
                    'v2-px-6': item.key === 'SPONSORING',
                  }
                )}
                role="button"
                onClick={() => handleTabClick(item.key)}
                style={{
                  minWidth: `${Math.max(item.name.length * 9 + (item.key === 'SPONSORING' ? 48 : 32), 100)}px`,
                }}
              >
                <div className="v2-whitespace-nowrap v2-text-center v2-w-full">{item.name}</div>
              </li>
            );
          } else {
            return (
              <li
                key={item.key}
                ref={el => (tabRefs.current[item.key] = el)}
                className={cn(
                  'v2-whitespace-nowrap v2-px-4 v2-h-[51px] v2-flex v2-justify-center v2-items-center v2-py-3 v2-text-center v2-font-sweet-sans-pro-medium v2-font-medium v2-text-sm v2-text-[#475467] v2-cursor-pointer',
                  stretchTabs ? 'v2-flex-1 v2-min-w-0' : 'v2-w-max',
                  {
                    '!v2-text-[#154734] !v2-border-b-[2px] !v2-border-[#154734] v2-bg-[#E2E8E6]':
                      activeTab === item.key,
                  }
                )}
                role="button"
                onClick={() => handleTabClick(item.key)}
              >
                {item.name}
              </li>
            );
          }
        })}
      </ul>

      {/* Right arrow */}
      <div className="v2-flex-shrink-0">
        <Image
          src={arrowIcon}
          alt="Scroll Right"
          className="v2-rotate-180"
          role="button"
          onClick={() => {
            const i = list.findIndex(item => item.key === activeTab);
            if (i < list.length - 1) handleTabClick(list[i + 1].key);
            scrollByAmount(150);
          }}
        />
      </div>
    </div>
  );
};

export default TabHeader;
